import React, {useState} from 'react';
import {Container, Title, Text, TermsContainer} from './styles';
import {theme} from '../../styles/darkTheme';
import FormField from '../../components/molecules/FormField';
import {useTranslation} from '../../context/language';
import * as Yup from 'yup';
import {Formik, Form} from 'formik';
import SubmitButton from '../../components/molecules/SubmitButton';
import {
  FIELD_CITY,
  FIELD_FIRST_LAST_NAME,
  FIELD_FIRST_NAME,
  FIELD_MIDDLE_NAME,
  FIELD_PROVINCE,
  FIELD_SECOND_LAST_NAME, TOPII_POLICY, TOPII_TERMS,
} from '../../utils/constants';
import DateField from '../../components/molecules/DateField';
import TermsText from '../../components/atoms/Text';
import Link from '../../components/atoms/Link';
import {useDispatch, useSelector} from 'react-redux';
import {RegistryState} from '../../redux/reducers/registry';
import {setRegistry} from '../../redux/actions/registry';
import {useNavigate} from 'react-router-dom';
import {formatDate} from '../../utils/getters';
import {RootState} from '../../redux/reducers';
import axios from 'axios';
import TermsModal from '../../components/molecules/TermsModal';
import PrivacyModal from '../../components/molecules/PrivacyModal';

const Register = () => {
  const {text, title} = useTranslation();
  const dispatch = useDispatch();
  const [doc, setDoc] = useState('');
  const [visible, setVisible] = useState(false);
  const [visibleT, setVisibleT] = useState(false);
  const history = useNavigate();
  const {
    firstName,
    middleName,
    firstLastName,
    secondLastName,
    birthDate,
    city,
    province,
  } = useSelector((state: RootState) => ({...state.registry}));

  const FONT_SIZE_14 = '14px';
  const LINE_HEIGHT_20 = '20px';
  const FONT_WEIGHT_BOLD = 'bold';

  const defaultValues: RegistryState = {
    firstName: firstName,
    middleName: middleName,
    firstLastName: firstLastName,
    secondLastName: secondLastName,
    birthDate: birthDate,
    city: city,
    province: province,
  };

  const handleSubmit = (values: RegistryState) => {
    dispatch(setRegistry(values));
    history('/confirm_registry');
  };

  const getTerms = async () => {
    try {
      const res = await axios.get(TOPII_TERMS)
      setDoc(res.data)
      setVisibleT(true)
    } catch (e) {
      console.log(e)
    }
  }

  const getPolicy = async () => {
    try {
      const res = await axios.get(TOPII_POLICY)
      setDoc(res.data)
      setVisible(true)
    } catch (e) {
      console.log(e)
    }
  }

  const getMaxDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    return date;
  };

  const diacritics = new RegExp('^[A-Za-z ]+$');

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required(text.requiredField(text.firstName))
      .matches(diacritics, text.characterValidField(text.firstName))
      .min(2)
      .label(text.firstName),
    middleName: Yup.string()
      .matches(diacritics, text.characterValidField(text.middleName))
      .label(text.middleName),
    firstLastName: Yup.string()
      .required(text.requiredField(text.firstLastName))
      .matches(diacritics, text.characterValidField(text.firstLastName))
      .min(2)
      .label(text.firstLastName),
    secondLastName: Yup.string()
      .matches(diacritics, text.characterValidField(text.secondLastName))
      .label(text.secondLastName),
    birthDate: Yup.date()
      .required(text.requiredField(text.birthDate))
      .max(getMaxDate(), text.birthDateMax)
      .label(text.birthDate),
    city: Yup.string()
      .required(text.requiredField(text.city))
      .matches(diacritics, text.characterValidField(text.city))
      .label(text.city),
    province: Yup.string()
      .required(text.requiredField(text.province))
      .matches(diacritics, text.characterValidField(text.province))
      .label(text.province),
  });

  return (
    <Container theme={theme}>
      <PrivacyModal content={doc} visible={visible} setVisible={setVisible} />
      <TermsModal content={doc} visible={visibleT} setVisible={setVisibleT} />
      <Title theme={theme}>{title.registerTigo}</Title>
      <Text theme={theme}>{text.registerTigoMsg}</Text>
      <Formik
        initialValues={defaultValues}
        validationSchema={validationSchema}
        onSubmit={values => {
          handleSubmit({
            ...values,
            birthDate: formatDate(new Date(values.birthDate)),
          });
        }}>
        {() => (
          <Form>
            <FormField
              type={'text'}
              label={text.firstName}
              placeholder={text.firstNamePlaceholder}
              name={FIELD_FIRST_NAME}
              defaultValue={firstName}
            />
            <FormField
              type={'text'}
              label={text.middleName}
              placeholder={text.middleNamePlaceholder}
              name={FIELD_MIDDLE_NAME}
              defaultValue={middleName}
            />
            <FormField
              type={'text'}
              label={text.firstLastName}
              placeholder={text.firstLastNamePlaceholder}
              name={FIELD_FIRST_LAST_NAME}
              defaultValue={firstLastName}
            />
            <FormField
              type={'text'}
              label={text.secondLastName}
              placeholder={text.secondLastNamePlaceholder}
              name={FIELD_SECOND_LAST_NAME}
              defaultValue={secondLastName}
            />
            <DateField
              type={'text'}
              label={text.birthDate}
              placeholder={text.birthDatePlaceholder}
              name={'birthDate'}
            />
            <FormField
              type={'text'}
              label={text.city}
              placeholder={text.cityPlaceholder}
              name={FIELD_CITY}
              defaultValue={city}
            />
            <FormField
              type={'text'}
              label={text.province}
              placeholder={text.provincPlaceholder}
              name={FIELD_PROVINCE}
              defaultValue={province}
            />
            <TermsContainer>
              <TermsText
                fontColor={theme.secondary}
                fontSize={FONT_SIZE_14}
                lineHeight={LINE_HEIGHT_20}>
                {text.readTerms}
                <Link
                  onClick={() =>
                    getTerms()
                  }
                  fontColor={theme.primary}
                  fontSize={FONT_SIZE_14}
                  fontWeight={FONT_WEIGHT_BOLD}
                  lineHeight={LINE_HEIGHT_20}>
                  {text.termsOfUse}
                </Link>
                {text.termsAnd}
                <Link
                  onClick={() =>
                    getPolicy()
                  }
                  fontColor={theme.primary}
                  fontSize={FONT_SIZE_14}
                  fontWeight={FONT_WEIGHT_BOLD}
                  lineHeight={LINE_HEIGHT_20}>
                  {text.privacyPolicy}
                </Link>
                {text.ofTigo}
              </TermsText>
            </TermsContainer>
            <SubmitButton title={text.continue} />
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default Register;
